import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { AlertCircle, Calendar, Timer, User } from 'lucide-react';

function KanbanTicketCard({ ticket, onClick, isDragging = false, customFields = [] }) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: ticket.ticket_id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const priorityColors = {
    high: 'bg-rose-50 text-rose-700 border-rose-200',
    medium: 'bg-blue-50 text-blue-700 border-blue-200',
    low: 'bg-stone-100 text-stone-600 border-stone-200',
  };

  const formatTime = (minutes) => {
    if (!minutes) return null;
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours < 24) return `${hours}h ${mins}m`;
    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;
    return `${days}d ${remainingHours}h`;
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={onClick}
      data-testid={`kanban-ticket-${ticket.ticket_id}`}
      className="bg-white border border-stone-200 rounded-xl p-4 shadow-soft hover:shadow-gold hover:border-primary/30 cursor-grab active:cursor-grabbing transition-all"
    >
      {/* Title */}
      <h4 className="font-semibold text-stone-800 mb-2 text-sm line-clamp-2">{ticket.title}</h4>

      {/* Description */}
      {ticket.description && (
        <p className="text-xs text-stone-500 mb-3 line-clamp-2">{ticket.description}</p>
      )}

      {/* Meta info */}
      <div className="space-y-2">
        {/* Priority Badge */}
        <div className="flex items-center justify-between">
          <span
            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border ${
              priorityColors[ticket.priority]
            }`}
          >
            {ticket.priority === 'high' && <AlertCircle className="w-3 h-3" strokeWidth={2} />}
            {ticket.priority}
          </span>
          {ticket.time_to_complete && (
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-50 text-emerald-700 rounded-lg text-xs font-medium">
              <Timer className="w-3 h-3" strokeWidth={1.5} />
              {formatTime(ticket.time_to_complete)}
            </span>
          )}
        </div>

        {/* Due date */}
        {ticket.due_date && (
          <div className="flex items-center gap-1.5 text-xs text-stone-600">
            <Calendar className="w-3 h-3" strokeWidth={1.5} />
            <span>{new Date(ticket.due_date + 'T12:00:00').toLocaleDateString()}</span>
          </div>
        )}

        {/* Assignee */}
        {ticket.assignee_name && (
          <div className="flex items-center gap-1.5 text-xs text-stone-600">
            <User className="w-3 h-3" strokeWidth={1.5} />
            <span className="truncate">{ticket.assignee_name}</span>
          </div>
        )}

        {/* Custom Fields */}
        {customFields.length > 0 && ticket.custom_fields && Object.keys(ticket.custom_fields).length > 0 && (
          <div className="flex flex-wrap gap-1 pt-1">
            {customFields.map((field) => {
              const values = ticket.custom_fields[field.field_id];
              if (!values || values.length === 0) return null;
              const labels = values.map(v => {
                const opt = field.options.find(o => o.value === v);
                return opt ? opt.label : v;
              });
              return labels.map((label, i) => (
                <span
                  key={`${field.field_id}-${i}`}
                  data-testid={`ticket-field-${field.field_id}`}
                  className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-violet-50 text-violet-700 border border-violet-200"
                  title={field.name}
                >
                  {label}
                </span>
              ));
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default KanbanTicketCard;
